import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";

const AUTHOR_EMAIL = "hello@chelsearaywriting.com";
const NETLIFY_FORM_NAME = "contact";

/** URL-encode a plain object for Netlify Forms submission. */
function encodeFormData(data) {
  return Object.keys(data)
    .map(
      (key) =>
        encodeURIComponent(key) + "=" + encodeURIComponent(data[key] ?? "")
    )
    .join("&");
}

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [botField, setBotField] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!emailOk) {
      toast.error("That email doesn't look quite right.");
      return;
    }

    setSubmitting(true);
    try {
      const body = encodeFormData({
        "form-name": NETLIFY_FORM_NAME,
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim(),
        message: message.trim(),
        "bot-field": botField,
      });

      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });

      if (!res.ok) {
        throw new Error(`Netlify Forms returned ${res.status}`);
      }

      setSubmitted(true);
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
      toast.success("Your message has set sail. Chelsea will be in touch.");
    } catch (err) {
      toast.error(
        err?.message ||
          "Something went wrong. Please try again, or email directly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-ocean-base reveal overflow-hidden"
      aria-label="Contact the author"
    >
      <div className="parchment-overlay absolute inset-0" aria-hidden="true" />
      <div className="mist opacity-40" aria-hidden="true" />

      <div className="relative max-w-5xl mx-auto px-6 md:px-10">
        <div className="text-center">
          <p className="font-accent text-[10px] text-[#c9a25a] reveal-up">Contact</p>
          <h2
            data-testid="contact-title"
            className="font-display text-4xl md:text-5xl lg:text-6xl text-[#f2e8d5] mt-4 reveal-blur"
          >
            Send a Letter
          </h2>
          <p className="mt-6 text-[#a8b4c0] max-w-xl mx-auto">
            Press, partnerships, blog tours, or simply to share what{" "}
            <em>Against All Waves</em> meant to you. Chelsea reads every note.
          </p>

          <a
            data-testid="contact-mailto"
            href={`mailto:${AUTHOR_EMAIL}`}
            className="mt-6 inline-flex items-center gap-2 font-accent text-[11px] text-[#c9a25a] hover:text-[#e8c888] transition-colors"
            aria-label={`Email Chelsea Ray at ${AUTHOR_EMAIL}`}
          >
            <Mail size={14} aria-hidden="true" />
            {AUTHOR_EMAIL}
          </a>
        </div>

        <div className="mt-14 max-w-3xl mx-auto">
          {submitted ? (
            <div
              data-testid="contact-success"
              className="border border-[#c9a25a]/40 bg-[#0a1118]/60 px-8 py-12 text-center"
            >
              <p className="font-display italic text-2xl text-[#e8d9b6]">
                Your message has set sail.
              </p>
              <p className="mt-3 text-[#a8b4c0]">
                Watch for a reply on the next favorable wind.
              </p>
              <button
                data-testid="contact-send-another"
                onClick={() => setSubmitted(false)}
                className="mt-8 font-accent text-[10px] text-[#c9a25a] hover:text-[#e8c888] transition-colors"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form
              data-testid="contact-form"
              name={NETLIFY_FORM_NAME}
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              onSubmit={onSubmit}
              noValidate
              className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8 text-left"
            >
              {/* Netlify Forms markers */}
              <input type="hidden" name="form-name" value={NETLIFY_FORM_NAME} />
              <p className="hidden" aria-hidden="true">
                <label>
                  Don&rsquo;t fill this out:
                  <input
                    name="bot-field"
                    value={botField}
                    onChange={(e) => setBotField(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </label>
              </p>

              <div>
                <label
                  htmlFor="contact-name"
                  className="font-accent text-[10px] text-[#c9a25a]"
                >
                  Your Name
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="parchment-input mt-2"
                  placeholder="Eleanor Whitlock"
                  required
                />
              </div>
              <div>
                <label
                  htmlFor="contact-email"
                  className="font-accent text-[10px] text-[#c9a25a]"
                >
                  Email Address
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="parchment-input mt-2"
                  placeholder="you@thesea.com"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label
                  htmlFor="contact-subject"
                  className="font-accent text-[10px] text-[#c9a25a]"
                >
                  Subject{" "}
                  <span className="lowercase tracking-normal text-[#a8b4c0]/60 italic">
                    (optional)
                  </span>
                </label>
                <input
                  id="contact-subject"
                  data-testid="contact-subject"
                  name="subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="parchment-input mt-2"
                  placeholder="A note about Against All Waves"
                />
              </div>
              <div className="md:col-span-2">
                <label
                  htmlFor="contact-message"
                  className="font-accent text-[10px] text-[#c9a25a]"
                >
                  Your Message
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message"
                  name="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  className="parchment-input mt-2 resize-none"
                  placeholder="Cast your message into the sea..."
                  required
                />
              </div>
              <div className="md:col-span-2 text-center mt-2">
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={submitting}
                  className="btn-gold btn-gold-solid disabled:opacity-60 disabled:cursor-not-allowed"
                  aria-label="Send your message to Chelsea Ray"
                >
                  <Send size={14} aria-hidden="true" />
                  {submitting ? "Sending…" : "Send Message"}
                </button>
                <p className="mt-5 text-xs text-[#a8b4c0]/70 italic">
                  Or write directly to{" "}
                  <a
                    href={`mailto:${AUTHOR_EMAIL}`}
                    className="text-[#c9a25a] hover:text-[#e8c888] transition-colors"
                  >
                    {AUTHOR_EMAIL}
                  </a>
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
